/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type React from "react"
import type { JSX } from "react"
import { useCallback, useEffect, useState } from "react"

import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { useViewModel } from "../app/app-hooks"

interface Props {
    onIconSelect: (file: File) => void
}

export const ApplicationTypeIconSelectView = ({ onIconSelect }: Props): JSX.Element => {
    const loggerVM = useViewModel(LoggerViewModel)

    const [iconUrl, setIconUrl] = useState<string>("")
    const [label, setLabel] = useState<string>("")
    const [dragging, setDragging] = useState(false)

    useEffect(() => {
        return () => {
            if (iconUrl) URL.revokeObjectURL(iconUrl)
        }
    }, [iconUrl])

    useEffect(() => {
        if (!dragging) {
            setLabel("Select icon file or drag and drop PNG / JPG file.")
        } else {
            setLabel("Drop file here.")
        }
    }, [dragging])

    const handleFile = useCallback(
        (file: File) => {
            if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                const nextUrl = URL.createObjectURL(file)
                setIconUrl(nextUrl)
                onIconSelect(file)
            } else {
                setLabel("Please select a PNG or JPG file")
            }
        },
        [onIconSelect]
    )

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            handleFile(selectedFile)
        } else {
            loggerVM.logErrorMessage("Selected file is invalid.")
        }
    }

    const handleDrop = useCallback(
        (evt: React.DragEvent<HTMLDivElement>) => {
            evt.preventDefault()
            evt.stopPropagation()
            setDragging(false)

            const file = evt.dataTransfer.files[0]
            handleFile(file)
        },
        [handleFile]
    )

    const handleDragOver = useCallback((evt: React.DragEvent<HTMLDivElement>) => {
        evt.preventDefault()
        evt.stopPropagation()
        setDragging(true)
    }, [])

    const handleDragLeave = useCallback((evt: React.DragEvent<HTMLDivElement>) => {
        evt.preventDefault()
        evt.stopPropagation()
        setDragging(false)
    }, [])

    return (
        <div
            className="border border-legacy-border rounded-legacy-md"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            <div className="p-legacy">
                <div className="mb-6">
                    {iconUrl && <img src={iconUrl} style={{ maxWidth: "80px", marginTop: "10px" }} />}
                    {!iconUrl && label}
                </div>

                <input type="file" accept="image/png, image/jpeg" onChange={handleFileChange} />
            </div>
        </div>
    )
}
